// =========================================================================
// useScrollAnchorHighlight
//
// Reads the current `useLocation().hash`, finds the matching DOM id, and
// scrolls it into view with `block: 'center'` then briefly toggles a
// `data-settings-flash` attribute so the global CSS rule in
// `apps/web/src/index.css` can paint a 2px ring around it for ~1500ms.
//
// Intended as a single mount point inside `SettingsLayout` via
// `<AnchorWatcher />` — every settings child route renders through the
// same Outlet so wiring it once covers the whole surface. No per-page
// boilerplate.
// =========================================================================

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const FLASH_MS = 1500;
// Settle delay matters because lazyWithReload routes mount before
// React Query has hydrated their sections. A single rAF + 50ms tick
// covers both the synchronous mount and the most common async
// finishing pass; we still re-try on rAF in case the element renders
// later (e.g. after a long-running query resolves).
const SCROLL_RETRY_MS = 50;
const MAX_RETRY_ATTEMPTS = 20; // 20 * 50ms = ~1s window

export function useScrollAnchorHighlight() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!id) return;

    let attempts = 0;
    let cancelled = false;
    let timer: number | null = null;

    function tick() {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.setAttribute('data-settings-flash', '');
        window.setTimeout(() => {
          if (!cancelled) el.removeAttribute('data-settings-flash');
        }, FLASH_MS);
        return;
      }
      attempts += 1;
      if (attempts >= MAX_RETRY_ATTEMPTS) return;
      timer = window.setTimeout(tick, SCROLL_RETRY_MS);
    }

    // Defer one frame so the new outlet has a chance to paint before
    // we look up the id — avoids a one-tick flash of the old DOM.
    const raf = window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [pathname, hash]);
}

export function AnchorWatcher() {
  useScrollAnchorHighlight();
  return null;
}
